import React, { forwardRef } from 'react';
import { useButton } from '@react-aria/button';
import { ButtonText, StyledButton, IconStyledButton, ButtonOverlay, StyledSpinner, SpinnerWrapper } from './styled';
import { useObjectRef } from '../../hooks/useObjectRef';
/**
 * @typedef {object} ButtonProps
 * @property {'small' | 'medium' | 'large'} [size = 'medium'] - Defines the size of the button. This can be 'small', 'medium', or 'large'.
 * @property {string} [color = 'primary'] - Defines the color of the button. This can be any valid color string.
 * @property {'first' | 'last'} [groupPosition] - If the button is part of a group, this defines its position within the group. This can be 'first', or 'last'.
 * @property {'filled' | 'outline' | 'transparent' | 'text'} [variant = 'filled'] - Defines the variant of the button. This can be 'text', 'outlined', or 'contained'.
 * @property {boolean} [isLoading] - If true, the button will display a loading spinner and be disabled.
 * @property {boolean} [isRound = false] - If true, the button will have rounded corners. If false, the button will have square corners.
 * @property {ReactNode} [StartIcon] - An icon to display at the start of the button. This should be a React node.
 * @property {string} [text] - The text to display on the button.
 * @property {ReactNode} [EndIcon] - An icon to display at the end of the button. This should be a React node.
 * @property {Object} rest - Any other props to pass to the button. These will be applied to the root element of the button.
 *
 * @returns {ReactNode} Button component
 */
const Button = forwardRef(function Button(props, ref) {
    const { text, variant = 'filled', StartIcon, EndIcon, color = 'primary', size = 'medium', isLoading = false, isRound, groupPosition, ...rest } = props;
    function getButtonType() {
        if ((StartIcon || EndIcon) && !text) {
            return IconStyledButton;
        }
        return StyledButton;
    }
    const ButtonType = getButtonType();
    const _ref = useObjectRef(ref);
    const { buttonProps } = useButton({}, _ref);
    return (React.createElement(ButtonType, { ref: _ref, size: size, "$color": color, groupPosition: groupPosition, variant: variant, isLoading: isLoading, isRound: isRound, ...buttonProps, ...rest },
        React.createElement(ButtonOverlay, { "$color": color, groupPosition: groupPosition, variant: variant, isLoading: isLoading, isRound: isRound }),
        isLoading && (React.createElement(SpinnerWrapper, null,
            React.createElement(StyledSpinner, { variant: variant }))),
        StartIcon,
        text && (React.createElement(ButtonText, { className: "text", variant: variant, isLoading: isLoading }, text)),
        EndIcon));
});
export default Button;
